<template>
  <b-container>
    <b-overlay :show="loading" no-wrap fixed />
    <b-row class="mb-2">
      <b-col cols="6">
        <h1>Imports & Exports</h1>
      </b-col>
    </b-row>
    <b-tabs nav-wrapper-class="nav-wrapper-class" content-class="mt-3">
      <b-tab title="Instructions" active class="pt-3">
        <h4 class="text-black">Overview</h4>
        <p>
          To bulk upload menus, use the csv templates on the Modifiers and Items
          tabs to import modifiers first, then items, then menu items. If you
          have any questions or issues with imports, reach out to
          <a href="mailto:hello@zuul.com" target="_blank">hello@zuul.com</a>.
        </p>
        <h4 class="text-black">Modifiers</h4>
        <ul>
          <li>
            To <strong>create new modifiers</strong>, download the template on
            the Modifiers tab, fill in all modifier information, then upload the
            file.
          </li>
          <li>
            To <strong>edit existing modifiers</strong>, export modifiers, make
            updates to the file, then upload the new version of the file. Filter
            by brand to export one brand at a time, or leave the brand filter
            blank to export all existing modifiers.
          </li>
          <li>
            To <strong>add new modifiers to items</strong>, first create the new
            modifiers, then assign the modifier keys to items on the items
            import. Modifiers must be created first because modifier keys are
            included in the items import, and the modifiers must exist in the
            system before attaching them to items.
          </li>
        </ul>
        <h4 class="text-black">Items</h4>
        <ul>
          <li>
            To <strong>create new items</strong>, download the
            <strong>Items</strong> template on the Items tab, fill in all item
            information, then upload the file.
          </li>
          <li>
            To <strong>edit existing items</strong>, export
            <strong>items</strong>, make updates to the file, then upload the
            new version of the file. Use the <strong>Items</strong> import to
            bulk edit item name, price, description, modifiers, status, and
            allergens.
          </li>
        </ul>
        <h4 class="text-black">Menu Items</h4>
        <ul>
          <li>
            To <strong>add existing items to specific menus</strong>, download
            the <strong>Menu Items</strong>
            template on the Items tab, fill in all information, then upload the
            file. Once items are uploaded, menu items must be created to attach
            items to specific menus. Each item can exist on multiple menus with
            different prices, categories, or names.
          </li>
          <li>
            To <strong>edit existing menu items</strong>, export
            <strong>Menu Items</strong>, make updates to the file, then upload
            the new version of the file. Use the
            <strong>Menu Items</strong> import to bulk edit categories, or
            change item prices on specific menus only.
          </li>
        </ul>
      </b-tab>
      <b-tab title="Modifiers" class="pt-3">
        <b-row>
          <b-col lg="6" sm="12" class="mb-3">
            <div class="base-form-col">
              <h5 class="mb-3 header-border pb-2">Export</h5>
              <b-form-group label="Use Modifier Template" class="mb-0">
                <b-button
                  variant="primary"
                  class="mb-1 mb-sm-3"
                  @click="exportFile('attributes', 1)"
                  >Download</b-button
                >
              </b-form-group>
              <b-row class="mt-1 mt-md-3">
                <b-col class="form-inline mt-1">
                  <custom-search-field
                    v-model="brands['attributes']"
                    class="mr-2 single-input min-80 mb-0"
                    :placeholder="'Filter by brand (optional)'"
                    :path-to-search="'brands'"
                  />
                  <b-button
                    variant="primary"
                    class="my-0 my-md-1"
                    @click="exportFile('attributes', 0)"
                    >Export</b-button
                  >
                </b-col>
              </b-row>
            </div>
          </b-col>
          <b-col lg="6" sm="12" class="mb-3">
            <div class="base-form-col">
              <h5 class="mb-3 header-border pb-2">Import</h5>
              <b-form-group label="Import File" class="mb-0">
                <b-form-file
                  v-model="files['attributes']"
                  placeholder="Choose a csv file or drop it here..."
                  drop-placeholder="Drop csv file here..."
                  accept="csv"
                />
                <p class="helper-text mb-2">Must be a .csv</p>
                <b-button
                  class="my-1"
                  variant="primary"
                  @click="importFile('attributes')"
                  >Upload</b-button
                >
              </b-form-group>
              <error-alert :errors="errors['attributes']" class="mt-3" />
              <div
                v-if="Object.keys(results['attributes']).length"
                class="success-text text-left mt-3"
              >
                <div
                  v-for="(resultGroup, resultId) in results['attributes']"
                  :key="resultId"
                  class="text-left"
                >
                  Brand ID: #{{ resultId }}
                  <ul class="mb-0 import-results">
                    <li
                      v-for="(resultValue, resultKey) in resultGroup"
                      :key="resultKey"
                      :class="{
                        error: resultKey === 'errors' && resultValue.length,
                      }"
                    >
                      {{ resultKey }}:
                      <template
                        v-if="resultKey !== 'errors' || resultValue.length"
                      >
                        {{ resultValue }}
                      </template>
                      <template v-else>None</template>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </b-col>
        </b-row>
      </b-tab>
      <b-tab title="Items" class="pt-3">
        <b-row>
          <b-col>
            <h2 class="text-black">Items</h2>
          </b-col>
        </b-row>
        <b-row class="mt-3">
          <b-col lg="6" sm="12" class="mb-3">
            <div class="base-form-col">
              <h5 class="mb-3 header-border pb-2">Export</h5>
              <b-form-group label="Use Item Template" class="mb-0">
                <b-button
                  variant="primary"
                  class="mb-1 mb-sm-3"
                  @click="exportFile('items', 1)"
                  >Download</b-button
                >
              </b-form-group>
              <b-row class="mt-1 mt-md-3">
                <b-col class="form-inline mt-1">
                  <custom-search-field
                    v-model="brands['items']"
                    class="mr-2 single-input min-80 mb-0"
                    :placeholder="'Filter by brand (optional)'"
                    :path-to-search="'brands'"
                  />
                  <b-button
                    variant="primary"
                    class="my-1"
                    @click="exportFile('items', 0)"
                    >Export</b-button
                  >
                </b-col>
              </b-row>
            </div>
          </b-col>
          <b-col lg="6" sm="12" class="mb-3">
            <div class="base-form-col">
              <h5 class="mb-3 header-border pb-2">Import</h5>
              <b-form-group label="Import File" class="mb-0">
                <b-form-file
                  v-model="files['items']"
                  placeholder="Choose a csv file or drop it here..."
                  drop-placeholder="Drop csv file here..."
                  accept="csv"
                />
                <p class="helper-text">Must be a .csv</p>
                <b-button variant="primary" @click="importFile('items')"
                  >Upload</b-button
                >
              </b-form-group>
              <error-alert :errors="errors['items']" class="mt-3" />
              <div
                v-if="Object.keys(results['items']).length"
                class="success-text text-left mt-3"
              >
                <div
                  v-for="(resultGroup, resultId) in results['items']"
                  :key="resultId"
                >
                  Brand ID: #{{ resultId }}
                  <ul class="mb-0 import-results">
                    <li
                      v-for="(resultValue, resultKey) in resultGroup"
                      :key="resultKey"
                      :class="{ error: resultKey === 'errors' }"
                    >
                      {{ resultKey }}: {{ resultValue }}
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </b-col>
        </b-row>
        <b-row class="mt-4">
          <b-col>
            <h2 class="text-black">Menu Items</h2>
          </b-col>
        </b-row>
        <b-row class="mt-3 mb-5">
          <b-col lg="6" sm="12" class="mb-3">
            <div class="base-form-col">
              <h5 class="mb-3 header-border pb-2">Export</h5>
              <b-form-group label="Use Menu Item Template" class="mb-0">
                <b-button
                  variant="primary"
                  class="mb-1 mb-sm-3"
                  @click="exportFile('menu-items', 1)"
                  >Download</b-button
                >
              </b-form-group>
              <b-row class="mt-1 mt-md-3">
                <b-col class="form-inline mt-1">
                  <custom-search-field
                    v-model="brands['menu-items']"
                    class="mr-2 single-input min-80 mb-0"
                    :placeholder="'Filter by brand (optional)'"
                    :path-to-search="'brands'"
                  />
                  <b-button
                    variant="primary"
                    class="my-1"
                    @click="exportFile('menu-items', 0)"
                    >Export</b-button
                  >
                </b-col>
              </b-row>
            </div>
          </b-col>
          <b-col lg="6" sm="12" class="mb-3">
            <div class="base-form-col">
              <h5 class="mb-3 header-border pb-2">Import</h5>
              <b-form-group label="Import File" class="mb-0">
                <b-form-file
                  v-model="files['menu-items']"
                  placeholder="Choose a csv file or drop it here..."
                  drop-placeholder="Drop csv file here..."
                  accept="csv"
                />
                <p class="helper-text">Must be a .csv</p>
                <b-button variant="primary" @click="importFile('menu-items')"
                  >Upload</b-button
                >
              </b-form-group>
              <error-alert :errors="errors['menu-items']" class="mt-3" />
              <div
                v-if="Object.keys(results['menu-items']).length"
                class="success-text text-left mt-3"
              >
                <div
                  v-for="(resultGroup, resultId) in results['menu-items']"
                  :key="resultId"
                >
                  Brand ID: #{{ resultId }}
                  <ul class="mb-0 import-results">
                    <li
                      v-for="(resultValue, resultKey) in resultGroup"
                      :key="resultKey"
                      :class="{
                        error: resultKey === 'errors' && resultValue.length,
                      }"
                    >
                      {{ resultKey }}:
                      <template
                        v-if="resultKey !== 'errors' || resultValue.length"
                      >
                        {{ resultValue }}
                      </template>
                      <template v-else>None</template>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </b-col>
        </b-row>
      </b-tab>
    </b-tabs>
  </b-container>
</template>

<script>
import ResourceAPI from '../../services/ResourceAPI';

export default {
  name: 'ImportExports',
  data() {
    return {
      loading: false,
      brands: {
        attributes: null,
        'menu-items': null,
        items: null,
      },
      files: {
        attributes: null,
        'menu-items': null,
        items: null,
      },
      results: {
        attributes: {},
        'menu-items': {},
        items: {},
      },
      errorMessages: {
        attributes: false,
        'menu-items': false,
        items: false,
      },
      errors: {
        attributes: {},
        'menu-items': {},
        items: {},
      },
    };
  },
  methods: {
    exportFile(type, templateOnly) {
      const me = this;
      const fileName = type === 'attributes' ? 'modifiers.csv' : `${type}.csv`;
      const data = {
        type,
        optionalFields: 0,
        explainationHeaders: 1,
        templateOnly,
        brand: me.brands[type] ? me.brands[type].id : null,
      };

      me.loading = true;

      ResourceAPI.downloadCSV('export', data, fileName)
        .then((response) => {
          me.loading = false;

          if (response === false) {
            me.exportError = true;
            return;
          }

          me.exportError = false;
        })
        .catch((error) => alert(error));
    },

    importFile(type) {
      const me = this;

      me.loading = true;

      ResourceAPI.uploadFile('import', {
        file: me.files[type],
        type,
        pretend: 0,
      }).then((response) => {
        me.loading = false;
        me.files[type] = null;

        if (response.success) {
          me.results[type] = response.data;
          me.errorMessages[type] = false;
          me.errors[type] = {};
        } else {
          me.results[type] = false;
          me.errors[type] = { errors: response.data };
        }
      });
    },
  },
};
</script>
